import type { LabelPrintBatchDetail, LabelPrintBatchStatusHistoryEntry } from "@shared/types";
import { parseISO } from "date-fns";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getBatchStatusLabel, getBatchStatusLabelKey, setBatchRetryProgressTestId } from "./batch-summary.utils";

interface BatchRetryProgressProps {
  readonly batch: LabelPrintBatchDetail;
}

const toDate = (value: string | undefined): Date | undefined => {
  if (!value) {
    return undefined;
  }
  const date = parseISO(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

/**
 * Tells the user how far a batch is while its labels are retried (how many are created, how many are tried again, which attempt
 * it is and when the next one starts), and why a batch has failed. It is shown for a batch that is retried or has failed.
 */
export function BatchRetryProgress({ batch }: BatchRetryProgressProps) {
  const { t } = useTranslation();
  const isRetrying = batch.batchStatus === "Failed-for-Retry";
  if (!isRetrying && !batch.failureReason) {
    return null;
  }
  const created = batch.labelCreatedOrderCount ?? batch.labelCreatedOrders;
  const total = batch.totalOrderCount ?? batch.totalOrders;
  const retrying = batch.retryingOrderCount ?? batch.failedForRetryOrders;
  const removedCount = batch.detachedPurchaseOrderIds?.length ?? 0;
  const nextRetryAt = isRetrying ? toDate(batch.nextRetryAt) : undefined;
  const history = batch.statusHistory ?? [];
  const Icon = isRetrying ? RefreshCw : AlertTriangle;

  return (
    <section
      className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm dark:border-amber-900 dark:bg-amber-950/40"
      data-testid={setBatchRetryProgressTestId("container")}
    >
      <h3 className="flex items-center gap-2 font-semibold">
        <Icon className="h-4 w-4" />
        {isRetrying ? t("views.batchSummary.retryProgress.title") : t("views.batchSummary.retryProgress.failedTitle")}
      </h3>
      {isRetrying && (
        <>
          <p data-testid={setBatchRetryProgressTestId("progress")}>
            {t("views.batchSummary.retryProgress.progress", { created, total, retrying })}
          </p>
          <p className="text-muted-foreground" data-testid={setBatchRetryProgressTestId("attempt")}>
            {batch.maxLabelPrintAttempts
              ? t("views.batchSummary.retryProgress.attempt", { attempt: batch.totalLabelPrintAttempts, max: batch.maxLabelPrintAttempts })
              : t("views.batchSummary.retryProgress.attemptNoMax", { attempt: batch.totalLabelPrintAttempts })}
            {nextRetryAt && <> · {t("views.batchSummary.retryProgress.nextRetry", { time: t("dates.time", { date: nextRetryAt }) })}</>}
          </p>
        </>
      )}
      {removedCount > 0 && (
        <p data-testid={setBatchRetryProgressTestId("removed")}>{t("views.batchSummary.retryProgress.removed", { count: removedCount })}</p>
      )}
      {batch.failureReason && (
        <p className="text-muted-foreground" data-testid={setBatchRetryProgressTestId("reason")}>
          {batch.failureReason}
        </p>
      )}
      {history.length > 0 && <BatchStatusHistory history={history} />}
    </section>
  );
}

function BatchStatusHistory({ history }: { readonly history: LabelPrintBatchStatusHistoryEntry[] }) {
  const { t } = useTranslation();
  return (
    <div data-testid={setBatchRetryProgressTestId("history")}>
      <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {t("views.batchSummary.retryProgress.history")}
      </h4>
      <ul className="mt-1 space-y-0.5">
        {history.map((entry) => {
          const labelKey = getBatchStatusLabelKey(entry.status);
          const status = labelKey ? t(`views.batchStatus.${labelKey}`) : getBatchStatusLabel(entry.status);
          const at = toDate(entry.at);
          return (
            <li key={`${entry.attempt}-${entry.status}-${entry.at}`}>
              {t("views.batchSummary.retryProgress.historyEntry", {
                attempt: entry.attempt,
                status,
                time: at ? t("dates.time", { date: at }) : entry.at,
              })}
              {entry.reason ? ` - ${entry.reason}` : ""}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
